import type { ReactNode } from 'react';
import { formatINR } from '@/utils/format';
import { STATUS_LABEL, type AssetStatus, type GstType } from '@/api/fixedAssetsApi';

export const inputCls =
  'w-full h-9 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] disabled:bg-slate-50 disabled:text-slate-400';
export const labelCls = 'block text-xs font-semibold text-slate-500 mb-1';
export const primaryBtn =
  'inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';
export const secondaryBtn =
  'inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50';
export const dangerBtn =
  'inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-red-200 bg-white text-sm font-medium text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50';
export const thCls = 'px-4 py-2.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap';
export const cardCls = 'bg-white border border-[#e2e8f0] rounded-xl';

export const errMsg = (err: any, fallback: string) => err?.response?.data?.message || err?.message || fallback;

export const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  const dt = new Date(d.includes('T') ? d : `${d}T00:00:00`);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const money = (n?: number | null) => formatINR(Number(n || 0));

export function Field({ label, required, hint, children, className = '' }: { label: string; required?: boolean; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label className={labelCls}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export function Section({ title, subtitle, children, actions }: { title: string; subtitle?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <div className={`${cardCls} p-5`}>
      <div className="flex items-start justify-between gap-3 mb-4 pb-3 border-b border-slate-100">
        <div>
          <h3 className="text-sm font-semibold text-[#1e293b]">{title}</h3>
          {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

export function StatusBadge({ status }: { status: AssetStatus }) {
  const s = STATUS_LABEL[status] || { label: status, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${s.cls}`}>{s.label}</span>;
}

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = 'max-w-lg',
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${size} max-h-[90vh] overflow-hidden flex flex-col border border-[#e2e8f0]`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
          <div>
            <h2 className="text-base font-bold text-[#1e293b]">{title}</h2>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">{children}</div>
        {footer && <div className="px-5 py-4 border-t border-[#e2e8f0] bg-slate-50 flex items-center justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
}

/** GST type + amount inputs with an optional ITC checkbox. */
export function GstFields({
  gstType,
  gstAmount,
  itcEligible,
  onChange,
  showItc = true,
  base,
}: {
  gstType: GstType;
  gstAmount: number;
  itcEligible?: boolean;
  onChange: (p: { gstType?: GstType; gstAmount?: number; itcEligible?: boolean }) => void;
  showItc?: boolean;
  base?: number;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
      <Field label="GST">
        <select value={gstType} onChange={(e) => onChange({ gstType: e.target.value as GstType, gstAmount: e.target.value === 'NONE' ? 0 : gstAmount })} className={inputCls}>
          <option value="NONE">No GST</option>
          <option value="CGST_SGST">CGST + SGST (same state)</option>
          <option value="IGST">IGST (other state)</option>
        </select>
      </Field>
      <Field label="GST Amount (₹)" hint={base && gstType !== 'NONE' ? (
        <span className="space-x-2">
          {[5, 12, 18, 28].map((r) => (
            <button key={r} type="button" onClick={() => onChange({ gstAmount: Math.round(base * r) / 100 })} className="text-[#4f46e5] hover:underline cursor-pointer">
              {r}%
            </button>
          ))}
        </span>
      ) : undefined}>
        <input type="number" min={0} value={gstAmount || ''} disabled={gstType === 'NONE'} onChange={(e) => onChange({ gstAmount: Number(e.target.value) })} className={inputCls} />
      </Field>
      {showItc && (
        <label className={`flex items-center gap-2 text-sm h-9 ${gstType === 'NONE' ? 'text-slate-300' : 'text-slate-700'} cursor-pointer select-none`}>
          <input type="checkbox" checked={!!itcEligible} disabled={gstType === 'NONE'} onChange={(e) => onChange({ itcEligible: e.target.checked })} className="w-4 h-4 rounded" />
          Claim input tax credit
        </label>
      )}
    </div>
  );
}

/** Dr / Cr lines preview. */
export function EntryPreview({ lines }: { lines: { side: 'DR' | 'CR'; account: string; amount: number }[] }) {
  const visible = lines.filter((l) => Math.abs(l.amount) >= 0.005);
  const dr = visible.filter((l) => l.side === 'DR').reduce((s, l) => s + l.amount, 0);
  const cr = visible.filter((l) => l.side === 'CR').reduce((s, l) => s + l.amount, 0);
  return (
    <div className="border border-dashed border-indigo-200 bg-indigo-50/30 rounded-xl p-3 text-xs space-y-1">
      <p className="font-semibold text-indigo-900 mb-1">Accounting entry</p>
      {visible.map((l, i) => (
        <div key={i} className={`flex justify-between gap-3 ${l.side === 'CR' ? 'pl-6' : ''}`}>
          <span className="text-slate-700">
            <span className={`font-mono font-bold mr-1.5 ${l.side === 'DR' ? 'text-blue-700' : 'text-purple-700'}`}>{l.side}</span>
            {l.account}
          </span>
          <span className="font-mono font-semibold text-[#1e293b]">{money(l.amount)}</span>
        </div>
      ))}
      {Math.abs(dr - cr) >= 0.01 && <p className="text-red-600 font-semibold">Not balanced: Dr {money(dr)} vs Cr {money(cr)}</p>}
    </div>
  );
}
